import React from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { X } from "../Icon"
import { cn } from "../../../lib/utils"

const DialogPrimitive = Dialog

export default function AppDialog({
  open,
  onOpenChange,
  title,
  header,
  description,
  children,
  footer,
  maxWidth = "520px",
  "data-testid": dataTestId,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-[999] bg-black/45",
            "animate-overlay-in",
          )}
        />
        <Dialog.Content
          data-testid={dataTestId}
          className={cn(
            "fixed left-1/2 top-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2",
            "w-[calc(100%-32px)] max-h-[85vh]",
            "max-sm:max-h-[calc(100dvh-32px)]",
            "flex flex-col",
            "rounded-lg bg-[var(--surface-card,#fff)] shadow-[0_8px_32px_rgba(0,0,0,0.18)]",
            "animate-dialog-in",
          )}
          style={{ maxWidth }}
          aria-describedby={description ? "app-dialog-desc" : undefined}
        >
          {/* Custom header variant — dark slate bg, white text, no border, rounded top */}
          {header ? (
            <div
              className={cn(
                "flex shrink-0 items-center gap-2 px-4 py-3",
                "rounded-t-lg bg-[var(--slate,#334155)] text-white",
              )}
            >
              <Dialog.Title asChild>
                <span className="sr-only">{title}</span>
              </Dialog.Title>
              <div className="flex-1">{header}</div>
              <Dialog.Close asChild>
                <button
                  className={cn(
                    "flex cursor-pointer items-center rounded p-1",
                    "border-none bg-transparent text-white/70",
                    "hover:bg-white/[0.12] hover:text-white",
                  )}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </Dialog.Close>
            </div>
          ) : (
            /* Default header — white bg, border-bottom */
            <div
              className={cn(
                "flex shrink-0 items-center justify-between",
                "px-5 pb-3 pt-4",
                "border-b border-[var(--border-subtle-2,#E2E8F0)]",
              )}
            >
              <Dialog.Title
                className={cn(
                  "text-[15px] font-semibold text-[var(--text-heading,#1E293B)]",
                )}
              >
                {title}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className={cn(
                    "flex cursor-pointer items-center rounded p-1",
                    "border-none bg-transparent text-[var(--text-secondary,#64748B)]",
                    "hover:bg-[var(--surface-page,#F1F5F9)]",
                  )}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </Dialog.Close>
            </div>
          )}

          {description && (
            <Dialog.Description
              id="app-dialog-desc"
              className="px-5 pt-2 text-[13px] text-[var(--text-secondary,#64748B)]"
            >
              {description}
            </Dialog.Description>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer && (
            <div
              className={cn(
                "flex shrink-0 justify-end gap-2 max-sm:flex-col",
                "border-t border-[var(--border-subtle-2,#E2E8F0)] px-5 pb-4 pt-3",
              )}
            >
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── Decomposed Dialog primitives (Shadcn pattern) ───────────────────────────
// These are ADDITIVE named exports only. The default export above is unchanged.

export const DialogRoot = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogPortal = DialogPrimitive.Portal

export const DialogOverlay = React.forwardRef(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[999] bg-black/45",
        "animate-overlay-in",
        className,
      )}
      {...props}
    />
  ),
)
DialogOverlay.displayName = "DialogOverlay"

export const DialogContent = React.forwardRef(
  ({ className, children, maxWidth = "520px", style, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2",
          "w-[calc(100%-32px)] max-h-[85vh]",
          "max-sm:max-h-[calc(100dvh-32px)]",
          "flex flex-col",
          "rounded-lg bg-[var(--surface-card,#fff)] shadow-[0_8px_32px_rgba(0,0,0,0.18)]",
          "animate-dialog-in",
          "focus:outline-none",
          className,
        )}
        style={{ maxWidth, ...style }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = "DialogContent"

/**
 * DialogHeader — two variants:
 *   variant="default" (light): white bg, bottom border, dark heading text
 *   variant="dark"           : dark slate bg, white text, no border, rounded top
 */
export const DialogHeader = React.forwardRef(
  ({ className, variant = "default", children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex shrink-0 items-center gap-2",
        variant === "dark"
          ? "px-4 py-3 rounded-t-lg bg-[var(--slate,#334155)] text-white"
          : "px-5 pb-3 pt-4 justify-between border-b border-[var(--border-subtle-2,#E2E8F0)]",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
)
DialogHeader.displayName = "DialogHeader"

export const DialogFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex shrink-0 justify-end gap-2 max-sm:flex-col",
      "border-t border-[var(--border-subtle-2,#E2E8F0)] px-5 pb-4 pt-3",
      className,
    )}
    {...props}
  />
))
DialogFooter.displayName = "DialogFooter"

export const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-[15px] font-semibold text-[var(--text-heading,#1E293B)]",
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = "DialogTitle"

export const DialogDescription = React.forwardRef(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(
        "px-5 pt-2 text-[13px] text-[var(--text-secondary,#64748B)]",
        className,
      )}
      {...props}
    />
  ),
)
DialogDescription.displayName = "DialogDescription"

/**
 * DialogClose — renders the X close button matching the existing AppDialog style.
 * Pass variant="dark" for use inside a dark (slate) DialogHeader.
 */
export const DialogClose = React.forwardRef(
  ({ className, variant = "default", ...props }, ref) => (
    <DialogPrimitive.Close
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center rounded p-1",
        "border-none bg-transparent",
        variant === "dark"
          ? "text-white/70 hover:bg-white/[0.12] hover:text-white"
          : "text-[var(--text-secondary,#64748B)] hover:bg-[var(--surface-page,#F1F5F9)]",
        className,
      )}
      aria-label="Close"
      {...props}
    >
      <X size={16} />
    </DialogPrimitive.Close>
  ),
)
DialogClose.displayName = "DialogClose"
